import * as XLSX from "xlsx";
import { getStatisticsResolvedDateRange } from "./statsFilterUtils";

function describeAppliedFilters(fv) {
  if (!fv || typeof fv !== "object") return [];
  const out = [];
  if (fv.period) out.push(`Period (API): ${fv.period}`);
  if (fv.scope) {
    out.push(
      `Scope: ${fv.scope}${fv.scopeId ? ` (${fv.scopeId})` : ""}`,
    );
  }
  if (fv.scopeType) {
    out.push(
      fv.scopeType === "global" || !fv.scopeId
        ? `Scope type: ${fv.scopeType}`
        : `Scope type: ${fv.scopeType} (${fv.scopeId})`,
    );
  }
  if (fv.loadStatus?.length) {
    out.push(`Load status: ${fv.loadStatus.join("; ")}`);
  }
  if (fv.search) out.push(`Search: ${fv.search}`);
  if (fv.userRole?.length) {
    out.push(`User roles: ${fv.userRole.join("; ")}`);
  }
  if (fv.userStatus?.length) {
    out.push(`User status: ${fv.userStatus.join("; ")}`);
  }
  if (fv.carrierStatus?.length) {
    out.push(`Carrier status: ${fv.carrierStatus.join("; ")}`);
  }
  if (fv.customerStatus?.length) {
    out.push(`Customer status: ${fv.customerStatus.join("; ")}`);
  }
  if (fv.customerType) out.push(`Customer type: ${fv.customerType}`);
  if (fv.shipmentType) out.push(`Shipment type: ${fv.shipmentType}`);
  return out;
}

export function buildStatisticsExportRows({
  tabTitle,
  selectedDateRange,
  filterValues,
  statCards,
  config,
  top5Users,
  includeTopUsers,
}) {
  const rows = [];
  rows.push([`${tabTitle} Statistics Export`]);
  rows.push([`Date range preset: ${selectedDateRange}`]);
  const resolvedRange = getStatisticsResolvedDateRange(
    filterValues,
    selectedDateRange,
  );
  if (resolvedRange?.from && resolvedRange?.to) {
    rows.push([`From: ${resolvedRange.from}`]);
    rows.push([`To: ${resolvedRange.to}`]);
  }
  describeAppliedFilters(filterValues).forEach((line) => rows.push([line]));
  rows.push([]);
  rows.push(["Summary cards"]);
  rows.push(["Title", "Value"]);
  for (const card of statCards || []) {
    rows.push([card.title, card.value]);
  }

  if (!config || typeof config !== "object") {
    return rows;
  }

  const { donut, bar, line } = config;

  if (donut && (donut.segments?.length || donut.total != null)) {
    rows.push([]);
    rows.push([donut.title || "Breakdown"]);
    rows.push(["Label", "Percent", "Count", "Amount"]);
    for (const seg of donut.segments || []) {
      rows.push([
        seg.label,
        seg.value,
        seg.count ?? "",
        seg.amount ?? "",
      ]);
    }
    rows.push([
      donut.totalLabel || "Total",
      "",
      "",
      donut.total ?? "",
    ]);
  }

  if (bar?.data?.length) {
    rows.push([]);
    rows.push([bar.title || "Bar chart"]);
    const keys = Object.keys(bar.data[0]);
    rows.push(keys);
    for (const row of bar.data) {
      rows.push(keys.map((k) => row[k]));
    }
  }

  if (line?.categories?.length && line?.series?.length) {
    rows.push([]);
    rows.push([line.title || "Trend"]);
    const header = ["Period", ...line.series.map((s) => s.name)];
    rows.push(header);
    const n = line.categories.length;
    for (let i = 0; i < n; i += 1) {
      const vals = [line.categories[i]];
      for (const s of line.series) {
        vals.push(s.data?.[i] ?? "");
      }
      rows.push(vals);
    }
  }

  if (includeTopUsers && top5Users?.length) {
    rows.push([]);
    rows.push(["Top 5 Users"]);
    rows.push(["Rank", "Name", "Loads Count"]);
    top5Users.forEach((u, i) => {
      rows.push([
        i + 1,
        (u.fullName || "").replace(/,/g, " "),
        u.loadsCount ?? 0,
      ]);
    });
  }

  return rows;
}

export function downloadStatisticsXlsx(rows, filenameBase) {
  if (!rows || rows.length === 0) return;
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Statistics");
  const out = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenameBase}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
